/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { assetPathFnState, environmentContextState } from '../../recoil/atoms';
import { StepViewModel } from '../models/step';
import { Option } from '../models/option';
import { MeasurementSystem } from '../enums/measurement-system.enum';
import { SwitchToggle } from './SwitchToggle.component';
import { InputType } from '../enums/input-type.enum';
import { RadioOption } from './RadioOption';
import { parseEngineVariables } from '../utils/engine-helper';
import { ResultViewRender } from '../views/ResultViewRender';
import { StepValues } from '../models/step-values';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { Size } from '../enums/size.enum';
import { convertStringToJsx } from '../utils/dom-helper';
import { SelectionType } from '../../enums/SelectionType.enum';
import { SubContext } from '../../enums/SubContext.enum';
import styles from './Step.component.module.scss';
import { isMobileState } from '../../recoil/selectors';
import { LeadGenerationForm } from '../../root/components/LeadGenerationForm';
import { useProductSelection } from '../../hooks/useProductSelection';
import { LazyImageLoader } from '../../product_detail/components/LazyImageLoader';
import { Button } from '../../root/components/buttons/Button';
import { LongSize } from '../../product_detail/components/size.enum';
import { Fit } from '../../enums/Fit.enum';

export interface StepProps {
  stepValues: StepValues;
  data: StepViewModel;
  style?: any;
  onOptionSelect: (option: Option) => void;
  activeMeasurement: MeasurementSystem;
  toggleMeasurementCallback: (measurementSystem: MeasurementSystem) => void;
  selectedOption: Option | undefined;
  productHandle: ProductHandle;
  size: Size;
  selectionType: SelectionType;
  selectionKey: string;
  subContext?: SubContext;
  onSizeSelect?: (size: LongSize, fit: Fit | undefined, confirmedForStepper: boolean) => void;
}

export const Step: React.FC<StepProps> = (props) => {
  const { product, variant } = useProductSelection(props.selectionType, props.selectionKey);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const environmentContext = useRecoilValue(environmentContextState);
  const isMobile = useRecoilValue(isMobileState);

  const [inputData, setInputData] = useState<any & { key: string }[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const handleChange = (option: Option) => () => {
    if (option && option.inputType === InputType.Number && option.unconvertedValue) {
      // TODO generalize this code to handle different units
      // convert kg to lbs before submitting
      const isMetric = props.activeMeasurement === MeasurementSystem.Metric;
      const modifier = isMetric ? 2.20462 : 1; // kg to lbs
      option.value = String(Math.round(Number(option.unconvertedValue) * modifier));
      props.onOptionSelect(option);
    } else if (option) {
      option.value = option.text ? option.text : option.imperialText;
      props.onOptionSelect(option);
    }
  };

  const handleInputChange =
    (option: Option, key: 'value' | 'unconvertedValue' = 'value') =>
    (e: any) => {
      const update: any = {};
      option[key] = e.target.value;
      if (props.data.key) {
        update[props.data.key] = e.target.value;
      }
      setErrorMsg('');
      setInputData({
        ...inputData,
        update,
      });
    };

  const handleKeyPress = (option: Option) => (event: any) => {
    if (event.key === 'Enter') {
      handleChange(option)();
    }
  };

  const generateAssetUrl = (assetName: string): string => {
    return assetPathFn(assetName);
  };

  if (props.data.render) {
    return props.data.render(props, handleChange) as JSX.Element;
  }

  const displayToggle = props.data?.options?.length > 0 && props.data.options[0].imperialText && props.data.options[0].metricText;

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepText}>
        {props.data.key === 'results' ? (
          <ResultViewRender size={parseEngineVariables(props.data.text, props.stepValues, props.productHandle)} />
        ) : (
          <span className={styles.subText}>{convertStringToJsx(parseEngineVariables(props.data.text, props.stepValues, props.productHandle))}</span>
        )}
      </div>
      {props.data.image && (
        <div className={styles.sizingImageWrapper}>
          <LazyImageLoader className={styles.sizingImage} src={`${generateAssetUrl(props.data.image)}`} alt="sizing illustration" />
        </div>
      )}
      {props.data.subText && <span className={styles.secondaryText}>{parseEngineVariables(props.data.subText, props.stepValues, props.productHandle)}</span>}
      {displayToggle && (
        <div className={styles.switchToggleContainer}>
          <SwitchToggle
            key="switch-toggle-1"
            optionA={MeasurementSystem.Imperial}
            optionB={MeasurementSystem.Metric}
            onOptionSelect={(selectedValue: MeasurementSystem) => {
              props.toggleMeasurementCallback(selectedValue);
            }}
            defaultOption={props.activeMeasurement}
            isWeightToggle={props.data.isWeightToggle}
          />
        </div>
      )}
      {props.data.options.map((option: Option, index: number) => {
        if (props.data.filter) {
          if (!props.data.filter(option, props.stepValues, props.productHandle)) {
            return;
          }
        }

        const measurementText = props.activeMeasurement && (props.activeMeasurement === MeasurementSystem.Imperial ? option.imperialText : option.metricText);
        const optionText: string = parseEngineVariables(measurementText ?? option.text, props.stepValues, props.productHandle);
        switch (option.inputType) {
          case InputType.Number:
            return (
              <>
                <input
                  type="number"
                  placeholder={optionText}
                  className={`${styles.inputPrimary} ${styles.email}`}
                  key={index}
                  onChange={handleInputChange(option, 'unconvertedValue')}
                  onKeyDown={handleKeyPress(option)}
                  defaultValue={option.unconvertedValue}
                />
                <Button text={'Continue'} buttonType="primary" disabled={!option.unconvertedValue} onClick={() => handleChange(option)()} />
              </>
            );
          case InputType.Text:
            return (
              <>
                <input
                  type="text"
                  placeholder={optionText}
                  className={`${styles.inputPrimary} ${styles.email}`}
                  key={index}
                  onChange={handleInputChange(option)}
                  onKeyDown={handleKeyPress(option)}
                  defaultValue={option.value}
                />
                <Button text={'Continue'} buttonType="primary" disabled={!option.value} onClick={() => handleChange(option)()} />
              </>
            );
          case InputType.Email:
            if (option.klaviyoKey === 'klaviyo4x5xId') {
              return (
                <LeadGenerationForm
                  size={'large'}
                  buttonMessage={'NOTIFY ME WHEN AVAILABLE'}
                  klaviyoFormKeys={environmentContext.klaviyo.size4x5xId}
                  productId={product.id}
                  variantId={variant?.id ?? ''}
                  textboxStyle={{ boxSizing: 'border-box', width: isMobile ? '100%' : '350px', height: '40px', marginBottom: '10px' }}
                />
              );
            }
            return (
              <input
                type="email"
                placeholder={optionText}
                className={`${styles.inputPrimary} ${styles.email}`}
                onClick={() => {
                  window.scrollTo(0, document.body.scrollHeight);
                }}
                onFocus={() => {
                  window.scrollTo(0, document.body.scrollHeight);
                }}
                key={index}
                onChange={handleInputChange(option)}
                onKeyDown={handleKeyPress(option)}
                defaultValue={option.value}
              />
            );
          case InputType.Button:
            return <Button text={optionText} key={index} onClick={handleChange(option)} fullWidth={true} buttonType="primary" size="large" />;
          case InputType.CTAButton:
            if (optionText.length > 20) {
              const parts = optionText.split(' ');
              const split = parts.reduce(
                (acc, curr, i) => {
                  if (i >= parts.length / 2) {
                    return { ...acc, bottom: [...acc.bottom, curr] };
                  } else {
                    return { ...acc, top: [...acc.top, curr] };
                  }
                },
                { top: [] as string[], bottom: [] as string[] },
              );

              return (
                <div key={index + 'drawer'} className={styles.ctaDrawer}>
                  <Button
                    key={index}
                    onClick={handleChange(option)}
                    fullWidth={true}
                    text={
                      <span>
                        {split.top.join(' ')}
                        <br />
                        {split.bottom.join(' ')}
                      </span>
                    }
                    buttonType="primary"
                    size="large"
                  />
                </div>
              );
            }

            return (
              <div key={index + 'drawer'} className={styles.ctaDrawer}>
                <Button key={index} onClick={handleChange(option)} fullWidth={true} text={optionText} buttonType="primary" size="large" />
              </div>
            );
          case InputType.SecondaryCTAButton:
            return (
              <div className={styles.secondaryCtaDrawer} key={index + 'drawer'}>
                <Button key={index} onClick={handleChange(option)} fullWidth={true} text={optionText} buttonType="secondary" size="large" />
              </div>
            );
          case InputType.Radio:
          default:
            return (
              <RadioOption
                key={index}
                option={option}
                onClick={handleChange(option)}
                isSelected={props.selectedOption === option}
                dataKey={props.data.key}
                activeMeasurement={props.activeMeasurement}
                stepValues={props.stepValues}
                productHandle={props.productHandle}
              />
            );
        }
      })}
      {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
    </div>
  );
};

export default Step;
